/**
 * Unicorn CDN upload utility.
 *
 * Upload passes through the Netlify function proxy `/.netlify/functions/unicorn-upload`
 * which injects the X-API-Key header server-side. The API key is NEVER exposed to the browser.
 */

/**
 * Normalized upload result. Unicorn's actual API returns `{ success, files: [{ url, id, ... }] }`
 * (array, even for single uploads). We normalize to a single-file shape for convenience.
 */
export interface CdnUploadResult {
  success: boolean;
  cdn_url: string;
  file: {
    id: number;
    original_name: string;
    stored_name: string;
    type: 'img' | 'video' | 'files';
    mime_type: string;
    size: number;
    width?: number;
    height?: number;
  };
}

/**
 * Raw Unicorn API response shape (as observed in real calls).
 */
interface UnicornUploadResponse {
  success: boolean;
  files: Array<{
    id: number;
    url: string;
    original_name: string;
    stored_name: string;
    type: 'img' | 'video' | 'files';
    mime_type: string;
    size: number;
    size_human?: string;
    width?: number;
    height?: number;
  }>;
}

export type CdnResourceType = 'image' | 'document' | 'video';

/**
 * Upload a file to the Unicorn CDN via the Netlify proxy function.
 * Uses XHR to support upload progress tracking.
 */
export function uploadToCdn(
  file: File,
  onProgress?: (percent: number) => void
): Promise<CdnUploadResult> {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append('file', file);

    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const raw = JSON.parse(xhr.responseText) as UnicornUploadResponse;
          if (!raw.success || !Array.isArray(raw.files) || raw.files.length === 0) {
            reject(new Error('CDN upload: unexpected response shape (no files[])'));
            return;
          }
          const f = raw.files[0];
          resolve({
            success: true,
            cdn_url: f.url,
            file: {
              id: f.id,
              original_name: f.original_name,
              stored_name: f.stored_name,
              type: f.type,
              mime_type: f.mime_type,
              size: f.size,
              width: f.width,
              height: f.height,
            },
          });
        } catch (err) {
          reject(new Error(`CDN upload: invalid JSON response (${xhr.status}): ${(err as Error).message}`));
        }
      } else {
        reject(new Error(`CDN upload failed (${xhr.status}): ${xhr.responseText}`));
      }
    });

    xhr.addEventListener('error', () => reject(new Error('CDN upload network error')));
    xhr.addEventListener('abort', () => reject(new Error('CDN upload aborted')));

    xhr.open('POST', '/.netlify/functions/unicorn-upload');
    xhr.send(formData);
  });
}

/**
 * Delete a file from the Unicorn CDN via the Netlify proxy function.
 */
export async function deleteFromCdn(fileId: number | string): Promise<void> {
  const res = await fetch(`/.netlify/functions/unicorn-delete?file_id=${encodeURIComponent(String(fileId))}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`CDN delete failed (${res.status}): ${body}`);
  }
}

/**
 * Detect Unicorn CDN URLs.
 * Example: https://cdn2.unicornsys.xyz/cdn/<project-key>/img/20240101_abc.jpg
 */
export function isUnicornCdnUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  return url.includes('cdn2.unicornsys.xyz');
}

/**
 * Extract the numeric file_id from a Unicorn CDN URL (for the DELETE API).
 *
 * NOTE: the Unicorn public URL does NOT embed the numeric file_id. To delete an asset,
 * the `file.id` returned at upload time must be persisted in your DB.
 * This helper is kept for future-proofing in case Unicorn adds id-in-URL support.
 * Currently returns null for URLs not enriched with `?file_id=...`.
 */
export function getFileIdFromUnicornUrl(url: string): string | null {
  if (!isUnicornCdnUrl(url)) return null;
  try {
    const parsed = new URL(url);
    const explicit = parsed.searchParams.get('file_id');
    if (explicit) return explicit;
    return null;
  } catch {
    return null;
  }
}

/**
 * Build an Unicorn URL with transformation params (width, height, format, quality, fit).
 * Returns the original URL unchanged if it's not a Unicorn CDN URL.
 *
 * Idempotent: calling twice with the same params does not stack them.
 */
export interface UnicornTransformOptions {
  w?: number;
  h?: number;
  format?: 'webp' | 'jpg' | 'png' | 'gif';
  q?: number;
  fit?: 'contain' | 'cover' | 'fill';
  download?: boolean;
}

export function getUnicornOptimizedUrl(
  url: string,
  options: UnicornTransformOptions = {}
): string {
  if (!isUnicornCdnUrl(url)) return url;
  try {
    const parsed = new URL(url);
    // Start from existing params so the function is idempotent:
    // re-calling it with new params overwrites previous values, not appends.
    if (options.w !== undefined) parsed.searchParams.set('w', String(options.w));
    if (options.h !== undefined) parsed.searchParams.set('h', String(options.h));
    if (options.format) parsed.searchParams.set('format', options.format);
    if (options.q !== undefined) parsed.searchParams.set('q', String(options.q));
    if (options.fit) parsed.searchParams.set('fit', options.fit);
    if (options.download) parsed.searchParams.set('download', '1');
    return parsed.toString();
  } catch {
    return url;
  }
}
